// UserContext.js - Gold-standard pattern with SWR
import { createContext, useContext, useMemo, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { axiosService } from 'shared/helpers/axios';



const UserContext = createContext();

// Use axiosService so the request hits the correct backend baseURL
// (plain fetch('/users/me/') would resolve to the frontend origin, not port 8000)
const fetcher = (url) => axiosService.get(url).then((res) => res.data);

// Canonical SWR key — must match the key used by useUser() in shared/helpers/axios.js
export const USER_ME_KEY = '/users/me/';

export function UserProvider({ children }) {
  
  // NOTE: Do NOT use useLocation() here. Using it causes UserProvider to re-render
  // on every navigation, which cascades to all context consumers and delays page transitions.
  // SWR handles 401s gracefully on public routes (shouldRetryOnError: false).
  
  // SWR handles all the caching, revalidation, and state management
  const { data: user, error, isLoading, isValidating, mutate: mutateUser } = useSWR(
    USER_ME_KEY,
    fetcher,
    {
      revalidateOnFocus: false, // Don't refetch on window focus
      revalidateOnReconnect: false, // Don't refetch on reconnect
      shouldRetryOnError: false, // Don't retry on auth errors
      dedupingInterval: 60000, // Cache for 1 minute to avoid duplicate requests
      errorRetryCount: 0, // Don't retry failed requests
    }
  );

  // When another browser window logs in a different user, the cookie changes.
  // The storage event fires in every OTHER window so we can detect this and
  // force SWR to revalidate /users/me/ — making all UI (including notifications)
  // consistent with whoever the server now considers authenticated.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'otable_auth_uid') return;
      // newValue null means logout; either way revalidate to get the current server state.
      mutateUser();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [mutateUser]);

  const refreshUser = () => {
    return mutateUser();
  };

  const clearUser = () => {
    return mutate(USER_ME_KEY, null, false);
  };

  // Memoize context value so consumers don't re-render unless data actually changes
  const contextValue = useMemo(() => ({
    user,
    loading: isLoading,
    isValidating,
    error,
    isAuthenticated: !!user,
    refreshUser,
    clearUser,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, isLoading, isValidating, error]);

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
}

// Utility functions for login/logout flow
// Call these after login/logout to update user state everywhere
export const refreshUserData = (userData) => {
  // If userData is provided, seed the cache immediately (no extra request)
  // If not, trigger a revalidation
  if (userData !== undefined) {
    return mutate(USER_ME_KEY, userData, false);
  }
  return mutate(USER_ME_KEY);
};

export const clearUserData = () => {
  return mutate(USER_ME_KEY, null, false);
};
